import { can } from '../utils/memberPermissions';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { followUpsAPI } from '../services/api';
import { followUpShortcut, snoozeFollowUpDate } from '../utils/followUps';
import FollowUpForm from './FollowUpForm';
import FormError from './FormError';

const FollowUpActions = ({ followUp, onSuccess }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState('');
  const mutation = useMutation({
    mutationFn: ({ complete, successMessage, ...changes }) => complete
      ? followUpsAPI.complete(followUp.id)
      : followUpsAPI.update(followUp.id, changes),
    onSuccess: async (_data, variables) => {
      setEditing(false);
      await Promise.all(['dashboard', 'lead', 'leads', 'inbox', 'followUps', 'activity'].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key], refetchType: key === 'dashboard' ? 'all' : 'active' })
      ));
      const successMessage = variables.successMessage || (variables.complete ? 'Task completed.' : 'Task saved.');
      setSuccess(successMessage);
      onSuccess?.(successMessage);
    },
  });

  const quickAction = async (changes) => {
    setMessage('');
    setSuccess('');
    try {
      await mutation.mutateAsync(changes);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not update task. Please try again.');
    }
  };

  if (!can(user, 'manageFollowUps')) return <p className="mt-3 text-sm text-gray-500">Tasks are read-only for your account.</p>;
  return (
    <div className="space-y-3 mt-3">
      <FormError message={message} />
      {success && <p role="status" aria-live="polite" className="text-sm font-medium text-green-700">{success}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={mutation.isPending} onClick={() => quickAction({ complete: true })}
          className={`flex-1 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-50`}>{mutation.isPending ? 'Updating...' : 'Mark Done'}</button>
        <button type="button" disabled={mutation.isPending}
          onClick={() => quickAction({ date: snoozeFollowUpDate(followUp.date, user?.timezone), time: followUp.dateOnly?'':followUp.time?.slice(0, 5), dateOnly:followUp.dateOnly||false, successMessage: 'Follow-up snoozed.' })}
          className={`flex-1 px-3 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium disabled:opacity-50`}>{followUp.date > followUpShortcut(0, user?.timezone) ? 'Snooze 1 day' : 'Snooze to tomorrow'}</button>
        <button type="button" disabled={mutation.isPending} aria-expanded={editing}
          onClick={() => setEditing(!editing)}
          className={`w-full px-3 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium disabled:opacity-50`}>Reschedule</button>
        <button type="button" disabled={mutation.isPending} onClick={()=>quickAction({status:'cancelled',successMessage:'Task cancelled.'})} className="w-full rounded-lg border px-3 py-2 text-sm text-gray-600">Cancel task</button>
      </div>
      {editing && <FollowUpForm initialValue={followUp} onCancel={() => setEditing(false)} onSubmit={(draft) => quickAction({ ...draft, successMessage: 'Follow-up rescheduled.' })} />}
    </div>
  );
};

export default FollowUpActions;
